import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Building2, Share2, Users } from 'lucide-react';
import { useEmpresa } from '@/context/EmpresaContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import CompartirDialog from '@/components/empresa/CompartirDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const initialFormData = {
  nombre: '',
  cif: '',
  direccion: '',
  telefono: '',
  email: '',
  dias_alerta: 30
};

export default function Empresas() {
  const { empresas, refreshEmpresas, setSelectedEmpresa, API } = useEmpresa();
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEmpresaLocal, setSelectedEmpresaLocal] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [isEditing, setIsEditing] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareEmpresa, setShareEmpresa] = useState(null);

  const openShareDialog = (empresa) => {
    setShareEmpresa(empresa);
    setShareDialogOpen(true);
  };

  const handleOpenDialog = (empresa = null) => {
    if (empresa) {
      setFormData({
        nombre: empresa.nombre || '',
        cif: empresa.cif || '',
        direccion: empresa.direccion || '',
        telefono: empresa.telefono || '',
        email: empresa.email || '',
        dias_alerta: empresa.dias_alerta ?? 30
      });
      setSelectedEmpresaLocal(empresa);
      setIsEditing(true);
    } else {
      setFormData(initialFormData);
      setSelectedEmpresaLocal(null);
      setIsEditing(false);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre.trim()) {
      toast.error('El nombre de la empresa es obligatorio');
      return;
    }

    setLoading(true);
    try {
      if (isEditing && selectedEmpresaLocal) {
        await axios.put(`${API}/empresas/${selectedEmpresaLocal.id}`, formData);
        toast.success('Empresa actualizada correctamente');
      } else {
        const response = await axios.post(`${API}/empresas`, formData);
        // Auto-select the new empresa
        setSelectedEmpresa(response.data);
        toast.success('Empresa creada correctamente');
      }
      setDialogOpen(false);
      refreshEmpresas();
    } catch (error) {
      console.error('Error saving empresa:', error);
      toast.error('Error al guardar la empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedEmpresaLocal) return;
    setLoading(true);
    try {
      await axios.delete(`${API}/empresas/${selectedEmpresaLocal.id}`);
      toast.success('Empresa eliminada correctamente');
      setDeleteDialogOpen(false);
      setSelectedEmpresaLocal(null);
      refreshEmpresas();
    } catch (error) {
      console.error('Error deleting empresa:', error);
      toast.error('Error al eliminar la empresa');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (empresa) => {
    setSelectedEmpresaLocal(empresa);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="empresas-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Outfit']">
            Empresas
          </h1>
          <p className="text-slate-500 mt-1">
            Gestiona tus clientes y empresas
          </p>
        </div>
        <Button 
          onClick={() => handleOpenDialog()} 
          className="btn-primary flex items-center gap-2"
          data-testid="add-empresa-btn"
        >
          <Plus className="w-4 h-4" />
          Nueva Empresa
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <Table data-testid="empresas-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-medium text-slate-500">Nombre</TableHead>
              <TableHead className="font-medium text-slate-500">CIF</TableHead>
              <TableHead className="font-medium text-slate-500">Dirección</TableHead>
              <TableHead className="font-medium text-slate-500">Teléfono</TableHead>
              <TableHead className="font-medium text-slate-500">Email</TableHead>
              <TableHead className="font-medium text-slate-500 text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {empresas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10">
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                      <Building2 className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-slate-500 mb-2">No hay empresas registradas</p>
                    <Button 
                      onClick={() => handleOpenDialog()} 
                      variant="outline"
                      size="sm"
                    >
                      Crear primera empresa
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              empresas.map((empresa) => {
                const isOwner = (empresa.my_role || 'owner') === 'owner';
                return (
                  <TableRow key={empresa.id} className="hover:bg-slate-50" data-testid={`row-empresa-${empresa.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <span>{empresa.nombre}</span>
                        {!isOwner && (
                          <Badge variant="outline" className="text-xs">
                            <Users className="h-3 w-3 mr-1" />
                            Compartida ({empresa.my_role === 'write' ? 'edición' : 'lectura'})
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{empresa.cif || '-'}</TableCell>
                    <TableCell>{empresa.direccion || '-'}</TableCell>
                    <TableCell>{empresa.telefono || '-'}</TableCell>
                    <TableCell>{empresa.email || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {isOwner && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openShareDialog(empresa)}
                            data-testid={`share-empresa-${empresa.id}`}
                          >
                            <Share2 className="w-4 h-4 text-slate-500" />
                          </Button>
                        )}
                        {isOwner && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(empresa)}
                            data-testid={`edit-empresa-${empresa.id}`}
                          >
                            <Pencil className="w-4 h-4 text-slate-500" />
                          </Button>
                        )}
                        {isOwner && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteDialog(empresa)}
                            data-testid={`delete-empresa-${empresa.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="dialog-empresa">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              {isEditing ? 'Editar Empresa' : 'Nueva Empresa'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Nombre de la empresa"
                data-testid="input-nombre-empresa"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cif">CIF</Label>
                <Input
                  id="cif"
                  value={formData.cif}
                  onChange={(e) => setFormData({ ...formData, cif: e.target.value })}
                  placeholder="B12345678"
                  data-testid="input-cif-empresa"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefono">Teléfono</Label>
                <Input
                  id="telefono"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  placeholder="+34 600 000 000"
                  data-testid="input-telefono-empresa"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="empresa@example.com"
                data-testid="input-email-empresa"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="direccion">Dirección</Label>
              <Input
                id="direccion"
                value={formData.direccion}
                onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                placeholder="Calle, número, ciudad"
                data-testid="input-direccion-empresa"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dias_alerta">Días de anticipación para alertas</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="dias_alerta"
                  type="number"
                  min="1"
                  max="365"
                  value={formData.dias_alerta}
                  onChange={(e) => setFormData({ ...formData, dias_alerta: parseInt(e.target.value) || 30 })}
                  className="w-24"
                  data-testid="input-dias-alerta-empresa"
                />
                <span className="text-sm text-slate-500">días antes del vencimiento/renovación</span>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="btn-primary" disabled={loading} data-testid="submit-empresa">
                {loading ? 'Guardando...' : isEditing ? 'Guardar Cambios' : 'Crear Empresa'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Compartir Dialog */}
      <CompartirDialog
        empresa={shareEmpresa}
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-dialog-empresa">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar empresa?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminarán la empresa y todas sus operaciones asociadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600" disabled={loading}>
              {loading ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
